import { GradientedScroller, connected, withErrorBoundary, NoResults } from 'shared'
import { absolutizePath } from 'shared/util'
import { TopLevelPage, ModelGrid } from 'ui'
import EventCard from 'ui/events/EventCard'
import ParkCard from 'ui/parks/ParkCard'
import { EventApi, AnnouncementApi, Park, ParkApi, Menu, User } from 'api'
import { addDays, format, startOfMonth, endOfMonth, isValid } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import Announcements from '../events/Announcements'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import adaptivePlugin from '@fullcalendar/adaptive'
import interactionPlugin from '@fullcalendar/interaction'

calendarGlobal = []
intervalId = null

CalendarView = ({match}) ->
  feedItemId = match.params.id
  [calendar, setCalendar] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ])
  [events, setEvents] = useState([])
  [showReservations, setShowReservations] = useState(false)
  [selectedPlaces, setSelectedPlaces] = useState([])
  [reservablePlaces, setReservablePlaces] = useState([])
  [selectedDate, setSelectedDate] = useState(new Date())
  [reservableDates, setReservableDates] = useState([])
  selectedPlacesRequestRef = useRef(null)
  announcements = AnnouncementApi.kick('index')

  parseDate = (dateTime) ->
    format(dateTime, "yyyy'-'MM'-'dd")

  dateParams = (calendar) ->
    {
      start_date: parseDate(calendar.startDate),
      end_date: parseDate(calendar.endDate)
    }

  applyFullCalendarAccessibilityFix = ->
    document.querySelectorAll('.fc-col-header-cell[role="columnheader"], .fc-col-header-cell[role="presentation"]').forEach (th) ->
      if not th.hasAttribute('scope')
        th.setAttribute('scope', 'col')

    document.querySelectorAll('th[role="presentation"]').forEach (th) ->
      if not th.hasAttribute('scope')
        th.setAttribute('scope', 'col')

    document.querySelectorAll('.fc-scrollgrid').forEach (table) ->
      cells = table.querySelectorAll('td, th')
      if cells.length <= 7
        table.removeAttribute('role')
        table.setAttribute('role', 'list')
        table.querySelectorAll('.fc-daygrid-day').forEach (cell) ->
          cell.setAttribute('role', 'listitem')
      else
        table.setAttribute('role', 'grid')

    document.querySelectorAll('.fc-daygrid-day-number').forEach (el) ->
      return if el.tagName.toLowerCase() is 'button'  # Skip if already converted

      dateLabel = el.getAttribute('aria-label') or el.textContent?.trim()
      button = document.createElement('button')
      button.className = el.className
      button.textContent = el.textContent?.trim() or ''
      button.setAttribute('type', 'button')
      button.setAttribute('tabindex', '0')
      button.setAttribute('aria-label', "Select date #{dateLabel or ''}")

      if el.id then button.id = el.id

      el.parentNode.replaceChild(button, el)

  hideCalendarLinks = ->
    document.querySelectorAll('.fc-daygrid-day-number').forEach (el) ->
      if el.tagName.toLowerCase() is 'a'
        el.style.display = 'none'

  useEffect(() ->
    setTimeout(applyFullCalendarAccessibilityFix, 1500)
    setTimeout(hideCalendarLinks, 50)
    reserveDate = sessionStorage.getItem('calendar-reserve-date')
    if reserveDate
      sessionStorage.removeItem('calendar-reserve-date')
      loadPlacesForDate(reserveDate)
    undefined
  , [])

  fetchReservableDates = (startDate = null, endDate = null) ->
    start = startDate or new Date()
    end = endDate or endOfMonth(new Date())

    ParkApi.availableDates({
      start_date: format(start, "yyyy-MM-dd"),
      end_date: format(end, "yyyy-MM-dd")
    }).then (dates) ->
      setReservableDates(dates.map((date) -> { date }))

  handleCalendarEventClick = (info) ->
    info.jsEvent?.preventDefault()
    info.jsEvent?.stopPropagation()

    if info.event.extendedProps?.openToReserve or info.event?.title?.includes("Open to Reserve")
      dateStr = info.event.startStr or format(new Date(info.event.start), "yyyy-MM-dd")
      sessionStorage.setItem('calendar-reserve-date', dateStr)
      application.go('/community-calendar') if feedItemId
      loadPlacesForDate(dateStr)
      return

    targetUid = info.event.extendedProps?.targetUid
    application.go("/community-calendar/#{targetUid}") if targetUid

  loadPlacesForDate = (date) ->
    normalizedDate = if typeof date == 'string'
      new Date("#{date}T00:00:00")
    else
      new Date(date.getFullYear(), date.getMonth(), date.getDate())
    formattedDate = format(normalizedDate, "yyyy-MM-dd")
    setSelectedDate(normalizedDate)

    # Ignore stale responses when user clicks dates quickly.
    requestKey = "#{formattedDate}-#{Date.now()}"
    selectedPlacesRequestRef.current = requestKey

    ParkApi.openAvailable({ start_date: formattedDate, end_date: formattedDate }).then (data) ->
      return unless selectedPlacesRequestRef.current == requestKey
      setSelectedPlaces(data.places or [])

  loadData = (calendar = null) ->
    calendar = {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date())
    } unless calendar
    EventApi.filtered(dateParams(calendar)).then (data) ->
      setEvents(data.events)
    fetchReservableDates(calendar.startDate, calendar.endDate)

  updateDateRange = (range) ->
    setSelectedDate(range.selection.startDate)
    setCalendar([range.selection])
    setCurrentPage(1)
    loadData(range.selection, 1)

  printCalendar = ->
    nav = document.querySelector('.MainNav')
    footer = document.querySelector('footer')

    nav?.style.setProperty('display', 'none', 'important')
    footer?.style.setProperty('display', 'none', 'important')

    restore = ->
      nav?.style.removeProperty('display')
      footer?.style.removeProperty('display')
      window.removeEventListener('afterprint', restore)

    window.addEventListener('afterprint', restore)

    window.print()

  useEffect ->
    handleKeyDown = (e) ->
      if (e.ctrlKey or e.metaKey) and e.key.toLowerCase() is 'p'
        e.preventDefault()
        printCalendar()
    window.addEventListener('keydown', handleKeyDown)
    -> window.removeEventListener('keydown', handleKeyDown)
  , []

  # Load the full displayed month, not FullCalendar's visible grid (which includes padding days).
  handleDatesSet = (info) ->
    monthStart = startOfMonth(info.view.currentStart)
    monthEnd = endOfMonth(info.view.currentStart)
    newRange =
      startDate: monthStart
      endDate: monthEnd
      key: 'selection'
    setCalendar([newRange])
    setSelectedDate(monthStart)
    loadData(newRange)

  calendarEvents = events.flatMap((model) ->
    base = "/app#/community-calendar"
    if model.category == "multiday"
      startDate = new Date("#{model.date}T09:00:00")  # Default start time at 9 AM
      endDate = new Date("#{model.end_date}T18:00:00")  # End time at 6 PM
      return [] unless isValid(startDate) and isValid(endDate)

      return {
        id: model.id
        targetUid:  model.targetUid
        title: model.name
        start: startDate.toISOString()
        end: endDate.toISOString()
        url: "#{absolutizePath(base)}/#{model.targetUid}" 
        allDay: true
      }

    else if model.is_recurring and model.occurrences?  # Handle recurring events
      return model.occurrences.map((occurrence) ->
        occurrenceDate = new Date("#{occurrence.date}T#{occurrence.time}:00")
        endTime = new Date("#{occurrence.date}T#{occurrence.close_time}:00")
        return null unless isValid(occurrenceDate) and isValid(endTime)

        return {
          id: occurrence.id
          targetUid:  model.targetUid
          title: model.name
          start: occurrenceDate.toISOString()
          end: endTime.toISOString()
          allDay: false
          url: "#{absolutizePath(base)}/#{model.targetUid}"
        }
      ).filter(Boolean)

    else  # Regular single-day events
      startDate = new Date("#{model.date}T#{model.open_time_local}:00")
      endDate = new Date("#{model.date}T#{model.close_time_local}:00")
      return [] unless isValid(startDate) and isValid(endDate)

      return {
        id: model.id
        targetUid:  model.targetUid
        title: model.name
        start: startDate.toISOString()
        end: endDate.toISOString()
        url: "#{absolutizePath(base)}/#{model.targetUid}"
        allDay: false
      }
  )

  calendarEvents.push(
    ...reservableDates.map((entry) ->
      {
        id: "reserve-#{entry.date}"
        title: "● Open to Reserve"
        start: entry.date
        allDay: true
        extendedProps: { openToReserve: true }
      }
    )
  )

  <TopLevelPage className="Calendar" modelId={feedItemId} base="/community-calendar" returnUrl="/community-calendar">
    <h1
      className="visually-hidden"
    >
      Community Calendar
    </h1>
    <div className="row calendar-section justify-content-center mt-3">
      <div className="col">
        <FullCalendar
          schedulerLicenseKey="0456860429-fcs-1763595698"
          plugins={[ dayGridPlugin, timeGridPlugin, adaptivePlugin, interactionPlugin]}
          initialView="dayGridMonth"
          fixedWeekCount={false}
          timeZone='local'
          customButtons={{
            printButton: {
              text: 'Print',
              click: -> printCalendar()
            }
          }}
          headerToolbar={{
            left: 'title',
            right: 'printButton today prev,next'
          }}
          datesSet={handleDatesSet}
          dateClick={(info) -> setSelectedDate(new Date(info.dateStr))}
          eventClick={handleCalendarEventClick}
          events={calendarEvents}
          height="auto"
        />
      </div>
    </div>

    {selectedPlaces.length > 0 &&
      <section>
        <div className="row justify-content-center">
          <div className="col-md-8">
            <h1 className="section-heading-lowercase my-3">Available Amenity Reservations</h1>
          </div>
        </div>

        <div className="row">
          {selectedPlaces.map((place, index) ->
            <div key={index} className="col-sm-6 col-lg-4 col-xl-3">
              <ParkCard
                park={place}
                base="/community-calendar"
                selectedDate={if selectedDate instanceof Date then format(selectedDate, "yyyy-MM-dd") else format(new Date(), "yyyy-MM-dd")}
              />
            </div>
          )}
        </div>
      </section>
    }
  </TopLevelPage>

export default withErrorBoundary connected CalendarView